import { useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useCallback, useContext, useState } from 'react';
import { UserContext } from '../../../contexts';

import Button from '../../Button';

interface PayButtonProps {
  onPay: (stripeToken: string) => Promise<void>;
  onError: (error: string) => void;
}

const useFullName = () => {
  const { user } = useContext(UserContext);
  if (!user) return '';

  const { firstName, lastName } = user;
  if (!(firstName && lastName)) return '';

  return `${firstName} ${lastName}`;
};

function PayButton({ onPay, onError }: PayButtonProps) {
  const stripe = useStripe();
  const elements = useElements();
  const fullName = useFullName();
  const [paymentIsInProgress, setPaymentIsInProgress] = useState(false);

  const onPayButtonClick = useCallback(async () => {
    if (!stripe || !elements) {
      throw new Error('Stripe not initialized');
    }

    setPaymentIsInProgress(true);

    try {
      const { token, error } = await stripe.createToken(elements.getElement('cardNumber')!, {
        name: fullName,
      });

      if (error || !token) {
        onError(error?.message || 'We could not process your payment. Please try again later.');
        throw new Error('Error processing payment');
      }

      await onPay(token.id);
    } finally {
      setPaymentIsInProgress(false);
    }
  }, [stripe, elements, onPay, fullName, onError]);

  return (
    <Button
      fullWidth
      size="lg"
      className="mt-4 mb-2"
      onClick={onPayButtonClick}
      loading={paymentIsInProgress}
      disabled={paymentIsInProgress || !stripe || !elements}
    >
      Pay
    </Button>
  );
}

export default PayButton;
